import {
  ArrowLeft,
  Check,
  CheckCircle,
  Info,
  MapPin,
  X,
} from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClipLoader } from 'react-spinners';
import { AccentButton } from '../../../../../../shared/Buttons';
import CityAutocomplete, {
  CityAutocompleteSelection,
} from '../../../../../../shared/CityAutocomplete';
import useApi from '../../../../../../utils/AxiosWrapper';
import {
  addDaysLocal,
  formatCoverageDate,
  parseIsoDateLocal,
  toIsoDateLocal,
} from '../../../../../../utils/coverageDate';
import { reverseGeocode } from '../../../../../../utils/reverseGeocode';
import {
  formatUSPhoneForDisplay,
  formatUSPhonePretty,
  parseUSPhoneToE164,
} from '../../../../../../utils/phone';
import { useUser } from '../../../../../../utils/UserContext';
import { Entity } from '../../../../../../types/apiTypes';
import { DISPATCH_TERMS_HTML } from '../../../../../../constants/dispatchTerms';
import {
  COVERAGE_TIMING_DAY_OFFSETS,
  COVERAGE_TIMING_OPTIONS,
  CoverageTiming,
  EQUIPMENT_TYPE_OPTIONS,
  EquipmentType,
  FUTURE_MIN_OFFSET_DAYS,
  LoadRequestPayload,
  PickupLocation,
  ZIP_REGEX,
} from '../../../../../../constants/loadRequest';
import {
  DISPATCH_PRICING_LABEL,
  DISPATCH_PRICING_UNAVAILABLE,
  formatDispatchPricing,
} from '../../../../../../utils/dispatchPricing';
import { useLoadRequestPricingQuery } from '../../../../../../hooks/useLoadRequestPricing';
import useBackdropDismiss from '../../../../../../hooks/useBackdropDismiss';
import {
  LOCATION_ERROR_MESSAGES,
  LocationError,
  classifyLocationError,
  getCurrentPosition,
  locationErrorTestId,
} from '../../../../../../shared/currentLocation';

// Load Board V1 — Web (THT-695). Single portal modal (ReferCarrierModal
// pattern) with an internal two-step flow: input → review. Mirrors the shipped
// mobile "Find Me Freight" sheet. The POST to the already-deployed
// POST /api/v1/emails/load-request happens only after the Terms & Services gate
// on the review step; MC#/contact are NEVER sent — the backend derives identity
// server-side from the auth token (schema is `.unknown(false)`).
//
// THT-753 adds one contact detail to that payload: `callback_phone`, the number
// the carrier explicitly confirms on the review step. It identifies nobody — it
// is the line the dispatcher rings back, prefilled from the customer record and
// editable per request, so a stale record phone stops sending dispatch to a dead
// line.

// DISPATCH_TERMS_HTML is a whole HTML document (it doubles as a mobile WebView
// payload). Injecting it as-is leaks: the parser drops the <html>/<head>/<body>
// tags but keeps the <style> element, so its `body{padding:16px 18px 40px;
// font-size:13px}` rule lands on the APP's body — restyling the page behind the
// overlay while the terms content itself gets none of that padding. Take only
// the body's contents and let Tailwind style them, scoped to the container.
const TERMS_BODY_HTML = DISPATCH_TERMS_HTML.replace(
  /^[\s\S]*<body[^>]*>|<\/body>[\s\S]*$/gi,
  ''
);

// Typography for the injected legal markup. Scoped with `[&_x]` descendant
// variants so nothing escapes the container, and `list-disc` is explicit
// because Tailwind's preflight strips list styling. The source wraps every
// bullet as <li><p>…</p></li>, so inner <p> margins are zeroed out.
const TERMS_PROSE_CLASSES = [
  'text-[13px] leading-relaxed text-gray-700',
  '[&_p]:my-2',
  '[&_strong]:font-semibold [&_strong]:text-primary',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_li]:my-1 [&_li>p]:my-0',
  '[&_h1]:mb-3 [&_h1]:mt-1 [&_h1]:text-center [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-primary',
  '[&_h2]:mb-1 [&_h2]:mt-5 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-primary',
  '[&_h3]:mb-1 [&_h3]:mt-4 [&_h3]:text-[13px] [&_h3]:font-semibold [&_h3]:text-primary',
].join(' ');

// Marks a field the Review gate actually requires (see `canReview`). The glyph
// is decorative — screen readers get the word instead, since a bare "*" is
// announced inconsistently (or not at all).
const RequiredMark = () => (
  <>
    <span aria-hidden className="ml-0.5 text-danger">
      *
    </span>
    <span className="sr-only"> (required)</span>
  </>
);

// Review step only. It used to be repeated under the confirmation's "Got it"
// button; John's round-3 call was that nothing renders below that button, so
// the policy is stated once, next to the rate it qualifies.
const FEE_DISCLAIMER =
  'Fees only charged for loads you accept. Nothing gets booked without your approval.';

// Callback-number section (THT-753), review step only. Copy lives here with the
// other review-step strings rather than at the call site.
const CALLBACK_LABEL = 'Callback number';
const CALLBACK_HELP =
  'This is the number your dispatcher will call. Change it if a different line is better.';
const CALLBACK_CONFIRM_LABEL = 'Call me at this number';
// Shown under the field once it has been visited — or, for a prefill nobody
// typed, as soon as it lands — and still doesn't hold a dialable US number.
// Empty counts, since the number is required.
const CALLBACK_ERROR =
  'Enter a valid US phone number so dispatch can reach you.';

type Step = 'input' | 'review';

interface RequestLoadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RequestLoadModal = ({ isOpen, onClose }: RequestLoadModalProps) => {
  const { callProtectedApi } = useApi();
  const { userRecord } = useUser();

  const [step, setStep] = useState<Step>('input');

  // --- Input-step state ---
  const [pickupLocation, setPickupLocation] = useState<PickupLocation | null>(
    null
  );
  const [manualInput, setManualInput] = useState('');
  const [locationError, setLocationError] = useState<LocationError>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentType[]>(
    []
  );
  const [coverageTiming, setCoverageTiming] = useState<CoverageTiming | null>(
    null
  );
  const [futureDate, setFutureDate] = useState<string | null>(null);

  // --- Review-step state ---
  // The number dispatch calls back on (THT-753). Held in the same pretty shape
  // both signup steps produce, so the prefill reads like something the customer
  // typed; `parseUSPhoneToE164` is what the gate and the payload go through.
  //
  // `userRecord` is the union of every account shape, and only the customer row
  // carries `phone_number` — the same column the backend falls back to. This
  // modal only ever renders on the customer dashboard, so anything else means
  // no number on file and the empty-field path below.
  const recordPhone =
    userRecord?.userType === Entity.Customer
      ? (userRecord.phone_number ?? '')
      : '';
  const prefillPhone = useMemo(
    () => formatUSPhoneForDisplay(recordPhone),
    [recordPhone]
  );
  const [callbackPhone, setCallbackPhone] = useState(prefillPhone);
  const [phoneConfirmed, setPhoneConfirmed] = useState(false);
  // Errors wait for the field to be visited: masking as-you-type means every
  // number is "invalid" until its tenth digit, and flagging that mid-keystroke
  // is the noise the signup phone fields deliberately avoid.
  const [phoneTouched, setPhoneTouched] = useState(false);
  // The record can land after the modal is already open (UserContext fetches
  // it), and it must never overwrite a number the customer has already edited
  // for this request.
  const phoneEditedRef = useRef(false);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);
  // `disabled={submitting}` only takes effect on the next render, so a fast
  // double-click can enter handleSubmit twice and send two dispatch requests. A
  // ref flips synchronously — the submit-once guard used on the banking flows.
  const isSubmittingRef = useRef(false);

  // Both async paths here outlive the modal easily: the geolocation race runs
  // up to 30s and the header X stays clickable throughout, and the submit POST
  // is on the network. Whatever the caller does with `isOpen`, once the modal
  // is closed nothing it started may write state — a late geolocation resolve
  // would re-fill the pickup (or plant an error banner) on top of `resetAll`,
  // and a late 200 would flip `submitted`, so the next open would land straight
  // on the "Request sent" view for a form the user never filled. Checked after
  // every await; a ref so the flip is synchronous, unlike state.
  const isAbandonedRef = useRef(false);
  useEffect(() => {
    if (isOpen) isAbandonedRef.current = false;
    return () => {
      isAbandonedRef.current = true;
    };
  }, [isOpen]);

  // Puts the record's number in the field and drops anything said about the
  // previous one. A prefill nobody typed gets its verdict straight away: a
  // stale or partial number on the record still looks filled in, so without the
  // error the disabled confirmation — and the disabled Submit behind it — have
  // no visible reason. A number the customer types is judged on blur instead
  // (see `onCallbackPhoneChange`).
  const seedCallbackPhone = useCallback(() => {
    setCallbackPhone(prefillPhone);
    setPhoneConfirmed(false);
    setPhoneTouched(!!prefillPhone && !parseUSPhoneToE164(prefillPhone));
    phoneEditedRef.current = false;
  }, [prefillPhone]);

  // Seeds the callback field from the customer record: on open, and again if
  // the record itself arrives late. An edit takes precedence over both.
  useEffect(() => {
    if (!isOpen || phoneEditedRef.current) return;
    seedCallbackPhone();
  }, [isOpen, seedCallbackPhone]);

  // Today, as the whole modal sees it — every displayed date, the Future floor,
  // and the submitted coverage_date derive from this one value so what the user
  // reads is always what gets sent.
  //
  // Keyed on `isOpen` rather than computed once at mount. RequestLoadEntry now
  // unmounts this on close, so the two usually coincide — but the modal must
  // not depend on that: any caller that keeps it mounted across opens would
  // otherwise pin the date forever, and a session left open overnight would
  // show — and submit — yesterday as "Same Day" with the Future floor a day
  // early.
  const todayIso = useMemo(
    () => toIsoDateLocal(new Date()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen]
  );

  // Dispatch fee tiers, fetched when the modal opens so the rate is ready by
  // the time the customer reaches review — the ONLY step that shows a number
  // (John, 2026-07-22). Through the app's React Query layer, so the result is
  // cached across opens and the fetch is cancelled/deduped for us rather than
  // by a hand-rolled effect.
  const { data: pricing } = useLoadRequestPricingQuery(isOpen);

  // The fee quote for the reviewed selection. Display only — never part of the
  // submitted payload, and never rendered outside the review step.
  const pricingSummary = useMemo(
    () =>
      pricing
        ? formatDispatchPricing(pricing, selectedEquipment)
        : DISPATCH_PRICING_UNAVAILABLE,
    [pricing, selectedEquipment]
  );

  const resetAll = useCallback(() => {
    setStep('input');
    setPickupLocation(null);
    setManualInput('');
    setLocationError(null);
    setLocationLoading(false);
    setSelectedEquipment([]);
    setCoverageTiming(null);
    setFutureDate(null);
    // Back to the record's number, not to blank: the next open starts from the
    // same prefill — and the same verdict on it — a first open does.
    seedCallbackPhone();
    setTermsAccepted(false);
    setTermsOpen(false);
    setSubmitting(false);
    setSubmitted(false);
    setHasError(false);
    isSubmittingRef.current = false;
  }, [seedCallbackPhone]);

  const handleClose = useCallback(() => {
    // Before resetAll, so anything already awaiting sees the flag and skips the
    // writes that would otherwise land after the reset.
    isAbandonedRef.current = true;
    resetAll();
    onClose();
  }, [onClose, resetAll]);

  const backdropDismiss = useBackdropDismiss(handleClose);

  // --- Location ---
  const useCurrentLocation = useCallback(async () => {
    setLocationLoading(true);
    setLocationError(null);
    try {
      const position = await getCurrentPosition();
      if (isAbandonedRef.current) return;
      const { latitude, longitude } = position.coords;

      const geo = await reverseGeocode(latitude, longitude);
      if (isAbandonedRef.current) return;
      const display =
        geo?.display ?? `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

      setPickupLocation({
        type: 'current',
        display,
        city: geo?.city,
        state: geo?.state,
        lat: latitude,
        lng: longitude,
      });
      // Reflect the fix in the manual input so editing/clearing it behaves like
      // manual entry.
      setManualInput(display);
    } catch (error) {
      if (isAbandonedRef.current) return;
      setLocationError(classifyLocationError(error));
    } finally {
      if (!isAbandonedRef.current) setLocationLoading(false);
    }
  }, []);

  const onManualChange = useCallback((text: string) => {
    setManualInput(text);
    const trimmed = text.trim();
    if (!trimmed) {
      // The input is the single source of truth (current-location fills it too),
      // so clearing it always clears the pickup location.
      setPickupLocation(null);
      return;
    }
    const isZip = ZIP_REGEX.test(trimmed);
    setPickupLocation({
      type: 'manual',
      display: trimmed,
      ...(isZip ? { zip: trimmed } : { city: trimmed }),
    });
  }, []);

  // A Places pick gives us structured parts, so we can skip onManualChange's
  // "is this a ZIP or a city?" guess and record exactly what Google resolved.
  const onSelectSuggestion = useCallback(
    (selection: CityAutocompleteSelection) => {
      setManualInput(selection.display);
      setPickupLocation({
        type: 'manual',
        display: selection.display,
        city: selection.city,
        state: selection.state,
        zip: selection.zip,
      });
    },
    []
  );

  // --- Equipment ---
  const toggleEquipment = useCallback((value: EquipmentType) => {
    setSelectedEquipment((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  // --- Coverage timing + date ---
  // The single date resolver every option goes through: fixed-offset options
  // (Same Day / Next Day) resolve from `todayIso`; Future resolves to whatever
  // the picker captured. One place decides, so the row, the gate, and the
  // submitted coverage_date can never disagree.
  const resolveCoverageDate = useCallback(
    (timing: CoverageTiming, picked: string | null): string | null => {
      const offset = COVERAGE_TIMING_DAY_OFFSETS[timing];
      if (offset === undefined) return picked;
      return toIsoDateLocal(addDaysLocal(parseIsoDateLocal(todayIso), offset));
    },
    [todayIso]
  );

  const optionDates = useMemo(() => {
    const dates: Partial<Record<CoverageTiming, string>> = {};
    for (const option of COVERAGE_TIMING_OPTIONS) {
      const iso = resolveCoverageDate(option.value, futureDate);
      if (iso) dates[option.value] = iso;
    }
    return dates;
  }, [resolveCoverageDate, futureDate]);

  // Earliest pickable Future date (today + 2, John's bound; no max).
  const futureMinIso = useMemo(
    () =>
      toIsoDateLocal(
        addDaysLocal(parseIsoDateLocal(todayIso), FUTURE_MIN_OFFSET_DAYS)
      ),
    [todayIso]
  );

  const coverageDate = coverageTiming
    ? (optionDates[coverageTiming] ?? null)
    : null;

  const canReview = useMemo(
    () =>
      !!pickupLocation?.display &&
      selectedEquipment.length >= 1 &&
      !!coverageTiming &&
      !!coverageDate,
    [
      pickupLocation?.display,
      selectedEquipment.length,
      coverageTiming,
      coverageDate,
    ]
  );

  const handleReview = useCallback(() => {
    if (!canReview) return;
    setStep('review');
  }, [canReview]);

  // --- Callback number (THT-753) ---
  // Masked as they type, exactly like the signup phone fields, so the prefill
  // and an edit are the same shape. One guard: `formatUSPhonePretty('')` hands
  // back a bare "+1", so a field the customer emptied stays empty instead of
  // refilling itself with a country code they didn't type.
  const onCallbackPhoneChange = useCallback((text: string) => {
    phoneEditedRef.current = true;
    const digits = text.replace(/\D/g, '');
    setCallbackPhone(digits ? formatUSPhonePretty(text) : '');
    // Any edit invalidates the confirmation: the number they agreed to be
    // called on is no longer the number in the field.
    setPhoneConfirmed(false);
    // And it re-arms the wait for a blur. Without this, one blur on a
    // half-typed number (or a bad prefill) leaves the field flagged for the
    // rest of the session, so every keystroke of the correction is scolded as
    // invalid until its tenth digit lands.
    setPhoneTouched(false);
  }, []);

  // The single parse the gate, the error and the payload all read, so the
  // number that enables Submit is the number that gets sent.
  const callbackE164 = useMemo(
    () => parseUSPhoneToE164(callbackPhone),
    [callbackPhone]
  );
  const showCallbackError = phoneTouched && !callbackE164;

  // --- Submit ---
  const payload: LoadRequestPayload | null = useMemo(() => {
    if (!pickupLocation || !coverageTiming || !coverageDate) return null;
    return {
      pickup_location: pickupLocation,
      equipment_types: selectedEquipment,
      coverage_timing: coverageTiming,
      coverage_date: coverageDate,
      // Optional by contract (the backend falls back to the record phone), so
      // the key is omitted rather than sent empty when there is nothing valid
      // to send. The submit gate means that can't happen in practice.
      ...(callbackE164 ? { callback_phone: callbackE164 } : {}),
    };
  }, [
    pickupLocation,
    selectedEquipment,
    coverageTiming,
    coverageDate,
    callbackE164,
  ]);

  const canSubmit = termsAccepted && phoneConfirmed && !!callbackE164;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || isSubmittingRef.current || !payload) return;
    isSubmittingRef.current = true;
    setHasError(false);
    setSubmitting(true);
    try {
      const response = await callProtectedApi(
        '/emails/load-request',
        'post',
        payload,
        'Something went wrong submitting your request.',
        undefined,
        undefined,
        0,
        // Suppress AxiosWrapper's toast: this modal renders its own in-context
        // error banner with a Retry action just below the button, so letting
        // the toast fire too showed the user two competing messages for one
        // failure. The banner is the single report.
        true
      );
      const status = response?.status;
      // The request itself still went through — the dispatcher gets it. We just
      // don't narrate the outcome into a modal the user already dismissed.
      if (isAbandonedRef.current) return;
      if (
        status !== undefined &&
        status >= HttpStatusCode.Ok &&
        status < HttpStatusCode.MultipleChoices
      ) {
        setSubmitted(true);
      } else {
        setHasError(true);
      }
    } catch (e) {
      console.error('Error occurred while submitting load request: ', e);
      if (!isAbandonedRef.current) setHasError(true);
    } finally {
      isSubmittingRef.current = false;
      if (!isAbandonedRef.current) setSubmitting(false);
    }
  }, [canSubmit, payload, callProtectedApi]);

  // --- Summaries (review) ---
  const equipmentSummary = useMemo(
    () =>
      EQUIPMENT_TYPE_OPTIONS.filter((o) => selectedEquipment.includes(o.value))
        .map((o) => o.label)
        .join(', '),
    [selectedEquipment]
  );

  const coverageSummary = useMemo(() => {
    const option = COVERAGE_TIMING_OPTIONS.find(
      (o) => o.value === coverageTiming
    );
    const label = option?.label ?? '';
    return coverageDate
      ? `${label} · ${formatCoverageDate(coverageDate)}`
      : label;
  }, [coverageTiming, coverageDate]);

  if (!isOpen) return null;

  const inputFieldClass =
    'w-full rounded-lg border bg-gray-100 p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-accent';

  const renderInputStep = () => (
    <div className="p-4">
      {/* Dispatch-service explanation — no MC#/contact fields; identity is
          resolved server-side. */}
      <div
        data-testid="requestLoad:dispatchBlurb"
        className="mb-4 rounded-lg border border-[#EAECF0] bg-gray-50 p-3 text-sm text-gray-600"
      >
        TACH Dispatching finds and books loads for you. We handle the searching,
        the broker calls, and the paperwork, so you can just drive.
      </div>

      {/* Location */}
      <h3 className="mb-2 text-sm font-semibold text-primary">
        Pickup location
      </h3>
      <button
        type="button"
        onClick={useCurrentLocation}
        disabled={locationLoading}
        className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-accent bg-white p-3 text-sm font-medium text-primary hover:bg-bgyellow disabled:cursor-not-allowed disabled:opacity-60"
      >
        {locationLoading ? (
          <ClipLoader color="#F7A826" size={16} data-testid="loader" />
        ) : (
          <>
            <MapPin size={18} />
            <span>Use current location</span>
          </>
        )}
      </button>
      {locationError && (
        // Amber note, not a red error: the field below is a first-class way to
        // answer this question, so a failed lookup is a detour, not a fault.
        <div
          data-testid={locationErrorTestId('requestLoad', locationError)}
          className="mb-3 flex items-start gap-2 rounded-lg bg-bgyellow p-2.5 text-xs text-gray-600"
        >
          <Info
            size={14}
            weight="fill"
            className="mt-px flex-shrink-0 text-accent"
          />
          <span>{LOCATION_ERROR_MESSAGES[locationError]}</span>
        </div>
      )}
      <label htmlFor="pickup" className="mb-1 block text-sm text-gray-500">
        City or ZIP
        <RequiredMark />
      </label>
      <CityAutocomplete
        id="pickup"
        value={manualInput}
        onChange={onManualChange}
        onSelect={onSelectSuggestion}
        placeholder="e.g. Dallas, TX or 75201"
        addClass={inputFieldClass}
        testId="requestLoad:manualInput"
      />

      {/* Equipment multi-select */}
      <h3 className="mb-2 mt-5 text-sm font-semibold text-primary">
        Equipment type
        <RequiredMark />
      </h3>
      <div className="flex flex-col gap-1">
        {EQUIPMENT_TYPE_OPTIONS.map((option) => {
          const checked = selectedEquipment.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => toggleEquipment(option.value)}
              data-testid={`requestLoad:equipment-${option.value}`}
              aria-pressed={checked}
              className="flex items-center gap-3 rounded-md px-1 py-2 text-left hover:bg-gray-50"
            >
              <span
                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-all ${
                  checked
                    ? 'border-accent bg-accent'
                    : 'border-gray-300 bg-white'
                }`}
              >
                {/* Dark check on the accent fill — white on #F7A826 fails
                    contrast, and it matches AccentButton's black-on-accent. */}
                {checked && (
                  <Check size={12} weight="bold" className="text-primary" />
                )}
              </span>
              <span className="text-sm text-gray-700">{option.label}</span>
            </button>
          );
        })}
      </div>

      {/* Coverage timing — required single-select */}
      <h3 className="mb-2 mt-5 text-sm font-semibold text-primary">
        Coverage needed
        <RequiredMark />
      </h3>
      <div className="flex flex-col gap-1">
        {COVERAGE_TIMING_OPTIONS.map((option) => {
          const selected = coverageTiming === option.value;
          const resolvedIso = optionDates[option.value];
          const resolvedDate = resolvedIso
            ? formatCoverageDate(resolvedIso)
            : undefined;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setCoverageTiming(option.value)}
              data-testid={`requestLoad:coverage-${option.value}`}
              aria-pressed={selected}
              className="flex items-center justify-between gap-3 rounded-md px-1 py-2 text-left hover:bg-gray-50"
            >
              <span className="flex items-center gap-3">
                <span
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border transition-all ${
                    selected ? 'border-accent' : 'border-gray-300'
                  }`}
                >
                  {selected && (
                    <span className="h-2.5 w-2.5 rounded-full bg-accent" />
                  )}
                </span>
                <span className="text-sm text-gray-700">{option.label}</span>
              </span>
              {resolvedDate && (
                <span
                  data-testid={`requestLoad:coverageDate-${option.value}`}
                  className="text-xs text-gray-500"
                >
                  {resolvedDate}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Future date picker — min today + 2 (no max). */}
      {coverageTiming === CoverageTiming.Future && (
        <div className="mt-3">
          <label
            htmlFor="futureDate"
            className="mb-1 block text-xs text-gray-500"
          >
            Pick a pickup date at least {FUTURE_MIN_OFFSET_DAYS} days out.
          </label>
          <input
            id="futureDate"
            type="date"
            min={futureMinIso}
            value={futureDate ?? ''}
            onChange={(e) => {
              const picked = e.target.value;
              // Belt-and-braces: `min` greys past days out, but guard anyway.
              setFutureDate(picked && picked >= futureMinIso ? picked : null);
            }}
            data-testid="requestLoad:futureDate"
            className={inputFieldClass}
          />
        </div>
      )}

      <div className="mt-6">
        <AccentButton
          onClick={handleReview}
          disabled={!canReview}
          addClass="w-full rounded-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Review request
        </AccentButton>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div className="p-4">
      <h3 className="mb-2 text-sm font-semibold text-primary">Your request</h3>
      <div className="mb-5 rounded-lg border border-[#EAECF0] bg-gray-50 px-3 py-1">
        <div className="py-2">
          <p className="mb-0.5 text-xs text-gray-500">Pickup location</p>
          <p
            data-testid="requestLoad:summaryPickup"
            className="text-sm font-medium text-gray-700"
          >
            {pickupLocation?.display}
          </p>
        </div>
        <div className="py-2">
          <p className="mb-0.5 text-xs text-gray-500">Equipment</p>
          <p
            data-testid="requestLoad:summaryEquipment"
            className="text-sm font-medium text-gray-700"
          >
            {equipmentSummary}
          </p>
        </div>
        <div className="py-2">
          <p className="mb-0.5 text-xs text-gray-500">Coverage needed</p>
          <p
            data-testid="requestLoad:summaryCoverage"
            className="text-sm font-medium text-gray-700"
          >
            {coverageSummary}
          </p>
        </div>
        {/* The one place in the flow that shows a number (John, 2026-07-22):
            not on the input step, not on the confirmation. Display only —
            never part of the submitted payload. */}
        <div className="py-2">
          <p className="mb-0.5 text-xs text-gray-500">
            {DISPATCH_PRICING_LABEL}
          </p>
          <p
            data-testid="requestLoad:summaryPricing"
            className="text-sm font-medium text-gray-700"
          >
            {pricingSummary}
          </p>
        </div>
      </div>

      {/* Fee policy, stated without a number (John, 2026-07-22). Smaller gray
          text: it is a reassurance the carrier can read at a glance, not a
          term they have to act on — the Terms checkbox below is the gate. */}
      <p
        data-testid="requestLoad:feeDisclaimer"
        className="mb-5 text-xs text-gray-500"
      >
        {FEE_DISCLAIMER}
      </p>

      {/* Callback number (THT-753). Prefilled from the customer record — the
          same column the backend falls back to — editable for this request, and
          confirmed explicitly, so no dispatcher calls a number the carrier
          didn't just read. Editing un-confirms; see `onCallbackPhoneChange`. */}
      <div className="mb-5">
        <label
          htmlFor="callbackPhone"
          className="mb-1 block text-sm font-semibold text-primary"
        >
          {CALLBACK_LABEL}
          <RequiredMark />
        </label>
        <p className="mb-2 text-xs text-gray-500">{CALLBACK_HELP}</p>
        <input
          id="callbackPhone"
          type="tel"
          inputMode="tel"
          value={callbackPhone}
          onChange={(e) => onCallbackPhoneChange(e.target.value)}
          onBlur={() => setPhoneTouched(true)}
          aria-invalid={showCallbackError}
          aria-describedby={
            showCallbackError ? 'callbackPhoneError' : undefined
          }
          data-testid="requestLoad:callbackPhone"
          className={`${inputFieldClass} ${
            showCallbackError ? 'border-danger' : ''
          }`}
        />
        {showCallbackError && (
          <p
            id="callbackPhoneError"
            data-testid="requestLoad:callbackError"
            className="mt-1 text-xs text-danger"
          >
            {CALLBACK_ERROR}
          </p>
        )}

        <div className="mt-3 flex items-start gap-3">
          <button
            type="button"
            role="checkbox"
            aria-checked={phoneConfirmed}
            // Nothing to confirm until the field holds a dialable number —
            // which is also the state a customer with no phone on file starts
            // in, so a blank can never be confirmed.
            disabled={!callbackE164}
            onClick={() => setPhoneConfirmed((prev) => !prev)}
            data-testid="requestLoad:callbackConfirm"
            className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
              phoneConfirmed
                ? 'border-accent bg-accent'
                : 'border-gray-300 bg-white'
            }`}
          >
            {phoneConfirmed && (
              <Check size={12} weight="bold" className="text-primary" />
            )}
          </button>
          <p className="text-sm text-gray-700">
            {CALLBACK_CONFIRM_LABEL}
            <RequiredMark />
          </p>
        </div>
      </div>

      {/* Required Terms & Services gate — enables the button only; nothing is
          stored server-side in V1. */}
      <div className="mb-5 flex items-start gap-3">
        <button
          type="button"
          role="checkbox"
          aria-checked={termsAccepted}
          onClick={() => setTermsAccepted((prev) => !prev)}
          data-testid="requestLoad:termsCheckbox"
          className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-all ${
            termsAccepted
              ? 'border-accent bg-accent'
              : 'border-gray-300 bg-white'
          }`}
        >
          {termsAccepted && (
            <Check size={12} weight="bold" className="text-primary" />
          )}
        </button>
        <p className="text-sm text-gray-700">
          I agree to the{' '}
          <button
            type="button"
            onClick={() => setTermsOpen(true)}
            data-testid="requestLoad:termsLink"
            className="text-blue-600 underline hover:text-blue-700"
          >
            TACH Dispatch Services Terms of Use
          </button>
          .
          <RequiredMark />
        </p>
      </div>

      {hasError && (
        <div
          data-testid="requestLoad:errorBanner"
          className="mb-3 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800"
        >
          Something went wrong submitting your request. Please try again.
        </div>
      )}

      <AccentButton
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
        addClass="w-full rounded-full py-3 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <ClipLoader color="#1D213B" size={18} data-testid="loader" />
        ) : hasError ? (
          'Retry'
        ) : (
          'Submit request'
        )}
      </AccentButton>
    </div>
  );

  const renderSuccessState = () => (
    <div
      data-testid="requestLoad:successView"
      className="flex flex-col items-center justify-center px-6 py-10 text-center"
    >
      <CheckCircle size={64} weight="fill" className="mb-4 text-green-600" />
      <h3 className="mb-2 text-lg font-semibold text-primary">
        You&apos;re in. We&apos;re on it.
      </h3>
      {/* Trimmed per the 2026-07-22 feedback: the equipment/lanes/rate
          preamble was restating what the carrier just filled in. */}
      <p
        data-testid="requestLoad:successBody"
        className="mb-6 text-sm text-gray-600"
      >
        A TACH dispatcher will call you within the next few hours. Please keep
        your phone handy.
      </p>
      <AccentButton onClick={handleClose} addClass="w-full rounded-full py-3">
        Got it
      </AccentButton>
    </div>
  );

  const headerTitle = submitted
    ? 'Request sent'
    : step === 'review'
      ? 'Review request'
      : 'Find Me Freight';

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50"
      data-testid="requestLoad:backdrop"
      // Clicking outside the panel closes it (see useBackdropDismiss).
      {...backdropDismiss}
    >
      <div className="max-h-[90%] w-full overflow-y-auto overflow-x-hidden rounded-[4px] bg-white sm:max-w-[95%] md:max-w-[500px]">
        <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-[#EAECF0] p-4 align-middle">
          <div className="flex items-center gap-2">
            {step === 'review' && !submitted && (
              <ArrowLeft
                className="hover:cursor-pointer hover:opacity-80"
                onClick={() => setStep('input')}
                size={20}
                data-testid="requestLoad:back"
              />
            )}
            <h2 className="text-[16px] font-[600] text-primary">
              {headerTitle}
            </h2>
          </div>
          <X
            className="hover:cursor-pointer hover:opacity-80"
            onClick={handleClose}
            weight="regular"
            size={24}
            data-testid="requestLoad:close"
          />
        </div>

        {submitted
          ? renderSuccessState()
          : step === 'input'
            ? renderInputStep()
            : renderReviewStep()}
      </div>

      {/* Full dispatch terms overlay — the agreement itself (bundled HTML,
          renders with no network), reused verbatim from the mobile constant. */}
      {termsOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-900 bg-opacity-50 p-4"
          data-testid="requestLoad:termsBackdrop"
          onClick={() => setTermsOpen(false)}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="TACH Dispatch Services Terms of Use"
            className="flex max-h-[85%] w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-w-[95%] md:max-w-[560px]"
            // Clicks inside must not reach the backdrop's dismiss handler.
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-shrink-0 flex-row items-center justify-between border-b border-[#EAECF0] px-6 py-4">
              <h2 className="text-[16px] font-[600] text-primary">
                Terms of Use
              </h2>
              <button
                type="button"
                onClick={() => setTermsOpen(false)}
                aria-label="Close terms"
                data-testid="requestLoad:termsClose"
                className="-mr-2 rounded p-1 text-primary hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            <div
              className={`overflow-y-auto px-6 py-4 ${TERMS_PROSE_CLASSES}`}
              data-testid="requestLoad:termsContent"
              // Static, bundled legal HTML — no user input, no network.
              dangerouslySetInnerHTML={{ __html: TERMS_BODY_HTML }}
            />
            <div className="flex flex-shrink-0 justify-end border-t border-[#EAECF0] px-6 py-3">
              <AccentButton
                onClick={() => setTermsOpen(false)}
                addClass="rounded-full px-6 !py-2 !text-[14px]"
              >
                Close
              </AccentButton>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default RequestLoadModal;
